import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sun, CalendarDays, Users, ListChecks, Settings as SettingsIcon, LogOut, Plus, Home } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 ${
        isActive
          ? "bg-[#2B2B2B] text-[#FDFBF7]"
          : "text-[#2B2B2B] hover:bg-[#EAE4D8]"
      }`
    }
  >
    <Icon className="w-4 h-4" strokeWidth={1.6} />
    {label}
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="app-shell">
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="brand-home-btn"
            className="flex items-center gap-2"
          >
            <Sun className="w-5 h-5 text-[#C25E3E]" strokeWidth={1.5} />
            <div className="leading-tight text-left">
              <div className="font-serif-villa text-lg">Casa Blunt</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#7A7A7A]">La Manga · España</div>
            </div>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/dashboard" icon={Home} label="Dashboard" testid="nav-dashboard" />
            <NavItem to="/calendar" icon={CalendarDays} label="Stays" testid="nav-calendar" />
            <NavItem to="/people" icon={Users} label="People" testid="nav-people" />
            <NavItem to="/operations" icon={ListChecks} label="Operations" testid="nav-operations" />
            {user?.is_admin && (
              <NavItem to="/settings" icon={SettingsIcon} label="Settings" testid="nav-settings" />
            )}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/bookings/new")}
              data-testid="new-booking-btn"
              className="hidden sm:inline-flex items-center gap-2 villa-btn-primary px-4 py-2 rounded-full text-sm"
            >
              <Plus className="w-4 h-4" strokeWidth={2} /> Nueva reserva
            </button>
            <div className="hidden sm:flex items-center gap-2 pl-2">
              {user?.picture && (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-[#E8E1D5]" />
              )}
              <div className="text-xs leading-tight">
                <div className="text-[#2B2B2B]">{user?.name}</div>
                <div className="text-[#9A9A9A]">{user?.email}</div>
              </div>
              <button
                onClick={logout}
                data-testid="logout-btn"
                className="ml-2 p-2 rounded-full hover:bg-[#EAE4D8] text-[#7A7A7A]"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        {/* mobile nav */}
        <div className="md:hidden border-t border-[#E8E1D5] overflow-x-auto">
          <div className="flex items-center gap-1 px-4 py-2 whitespace-nowrap">
            <NavItem to="/dashboard" icon={Home} label="Dashboard" testid="nav-dashboard-m" />
            <NavItem to="/calendar" icon={CalendarDays} label="Stays" testid="nav-calendar-m" />
            <NavItem to="/people" icon={Users} label="People" testid="nav-people-m" />
            <NavItem to="/operations" icon={ListChecks} label="Operations" testid="nav-operations-m" />
            {user?.is_admin && (
              <NavItem to="/settings" icon={SettingsIcon} label="Settings" testid="nav-settings-m" />
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <Outlet />
      </main>

      <footer className="max-w-7xl mx-auto px-5 sm:px-8 py-12 text-xs text-[#9A9A9A] tracking-wider">
        Hecho con cariño · Family use only
      </footer>
    </div>
  );
}
